import type { AiFetsApiClient } from '../aiFetsClient';
import type { PreInitResult } from '../../types/moved-types';

type ViewMode = 'preview' | 'ael' | 'public' | 'scorm';

type Params = {
  viewMode: ViewMode;
  guid: string;
};

type Result = PreInitResult | 'not_found' | null;

export type ApiFunction = (params: Params, signal?: AbortSignal) => Promise<Result>;

export const createGetPreInitResponse = (apiClient: AiFetsApiClient): ApiFunction => {
  const getPreInitResponse = async (params: Params, signal?: AbortSignal): Promise<Result> => {
    const response = await sendRequest(apiClient, params, signal);

    return convertResponse(response);
  };

  return getPreInitResponse;
};

const sendRequest = (apiClient: AiFetsApiClient, params: Params, signal?: AbortSignal): Promise<Response> => {
  const options = { ...(signal && { signal }) };

  switch (params.viewMode) {
    case 'preview':
      return apiClient['/api/editor/get-project/init/editor-preview/{courseGuid}'].get({
        params: { courseGuid: params.guid },
        ...options,
      });
    case 'ael':
      return apiClient['/api/editor/get-project/init/ael-student/{courseGuid}'].get({
        params: { courseGuid: params.guid },
        ...options,
      });
    case 'scorm':
      return apiClient['/api/editor/get-project/init/dispatched-scorm/{courseGuid}'].get({
        params: { courseGuid: params.guid },
        ...options,
      });
    case 'public':
      return apiClient['/api/editor/get-project/init/public/{guid}'].get({
        params: { guid: params.guid },
        ...options,
      });
  }
};

/**
 * Read as a plain Response so that the four init routes, which share the AppPreInitData body,
 * are converted in one place; the body is typed with the hand-written PreInitResult.
 */
const convertResponse = async (response: Response): Promise<Result> => {
  if (response.status === 200) {
    return (await response.json()) as PreInitResult;
  }

  if (response.status === 404) {
    return 'not_found';
  }

  return null;
};
